import React, { useState, useEffect } from 'react';
import './HomePage.css';
import { Page } from './Navigation';

// Images
const heroImage = require('../assets/jetty1.jpg');
const exploreImage = require('../assets/seal2.jpg');
const islandImage1 = require('../assets/peter_rob_dolphins.jpg');
const islandImage2 = require('../assets/dolphin1.jpg');
const fishingImage = require('../assets/fishing1.jpg');
const relaxImage = require('../assets/veranda1.jpg');

type RequireContext = {
  keys: () => string[];
  (id: string): unknown;
};
const slideshowContext = (require as any).context(
  '../assets/slideshow',
  false,
  /\.(png|jpe?g|gif|webp|svg)$/i
) as RequireContext;
const slideshowImages: string[] = slideshowContext.keys().map((key: string) => {
  const mod = slideshowContext(key) as any;
  return (mod?.default ?? mod) as string;
});

interface HomePageProps {
  onNavigate: (page: Page) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onNavigate }) => {
  const [blurAmount, setBlurAmount] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      // Calculate blur based on scroll position
      // Max blur of 10px when scrolled 500px
      const maxBlur = 10;
      const maxScroll = 500;
      const blur = Math.min((scrollPosition / maxScroll) * maxBlur, maxBlur);
      setBlurAmount(blur);
    };

    window.addEventListener('scroll', handleScroll);
    // Set initial state
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-advance slideshow
  useEffect(() => {
    if (slideshowImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div 
          className="hero-background"
          style={{ 
            backgroundImage: `url(${heroImage})`,
            filter: `blur(${blurAmount}px)`,
            transform: `scale(${1 + blurAmount * 0.01})`
          }}
        ></div>
        <div className="hero-image-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Start your adventure at the<br />
            <span className="hero-title-large">ABROLHOS ISLANDS</span>
          </h1>
          <button 
            className="hero-cta-button"
            onClick={() => onNavigate('booking')}
          >
            Book Your Stay
          </button>
        </div>
      </section>

      {/* Main Content */}
      <section className="content-section">
        <div className="content-container">
          <div className="section-header">
            <h2>EXPERIENCE THE ABROLHOS LIKE A LOCAL</h2>
            <div className="section-divider"></div>
            <p className="section-tagline">EXPLORE, RELAX, ENJOY</p>
            <p className="section-description">
              Whether you're after an oceanfront camp with panoramic ocean views or a quiet retreat, we've got you covered.
              Start with choosing the perfect home for your Abrolhos Islands adventure.
            </p>
            <button 
              className="section-cta-button"
              onClick={() => onNavigate('booking')}
            >
              View our Camps
            </button>
          </div>
        </div>
      </section>

      {/* Vacation Properties Section */}
      <section className="properties-section">
        <div className="content-container">
          <h2 className="section-title">Unlike Anywhere Else</h2>
          <p className="section-intro">
             The Abrolhos offers an experience unlike anything else. No matter where you've been, it has something unique to offer, come and see.
          </p>

          <div className="property-cards">
            <div className="property-card">
              <div className="property-image">
                <div className="property-image">
                    <img className="property-image" src={exploreImage} alt="Property" />
                </div>
              </div>
              <div className="property-content">
                <h3>Explore</h3>
                <p>
                The Abrolhos Islands offer an unparalleled frontier for exploration where you can wander through historic shipwreck sites, 
                discover rare bird colonies, and snorkel across vibrant coral gardens that feel entirely untouched by the modern world.
                </p>
              </div>
            </div>

            <div className="property-card">
              <div className="property-image-flex">
                    <img className="property-image-flex" src={fishingImage} alt="Property" />
                </div>
              <div className="property-content">
                <h3>Fish</h3>
                <p>
                  The Abrolhos Islands is widely considered one of the best locations in Australia for fishing,
                   where the water quality and diet of the local reef systems produce some of the highest-quality seafood in the world.
                </p>
              </div>
            </div>

            <div className="property-card">
                <div className="property-image">
                    <img className="property-image" src={relaxImage} alt="Property" />
                </div>
              <div className="property-content">
                <h3>Relax</h3>
                <p>
                When it comes to relaxing, the islands provide a profound sense of isolation where the only schedule is dictated by the tide, 
                allowing you to unwind on pristine coral atolls surrounded by nothing but the rhythmic lap of the Indian Ocean and the quiet solitude of a remote island sanctuary.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Text Left / Image Right Section */}
      <section className="home-page-info-container">
        <div className="info-section-container">
          <div className="info-content">
            <h2>Experience Island Life</h2>
            <p>
              The Abrolhos Islands offer a unique opportunity to disconnect from the modern world 
              and reconnect with nature. Our camps provide comfortable accommodations while maintaining 
              the authentic island experience that makes this destination so special.
            </p>
            <p>
              Whether you're here for fishing, diving, bird watching, or simply to unwind, 
              you'll find yourself surrounded by pristine marine environments and abundant wildlife.
            </p>
          </div>
          <div className="info-image home-info-image--in-column">
            <img src={islandImage1} alt="Abrolhos Islands" />
          </div>
        </div>
      </section>

      <div className="home-info-images-pair-mobile">
        <div className="info-image">
          <img src={islandImage1} alt="Abrolhos Islands" />
        </div>
        <div className="info-image">
          <img src={islandImage2} alt="Island wildlife" />
        </div>
      </div>

      {/* Image Left / Text Right Section */}
      <section className="home-page-info-container">
        <div className="info-section-container">
          <div className="info-image home-info-image--in-column">
            <img src={islandImage2} alt="Island Wildlife" />
          </div>
          <div className="info-content">
            <h2>Wildlife & Nature</h2>
            <p>
              The Abrolhos Islands are home to diverse marine life, seabirds, and unique island ecosystems. 
              From sea lions basking on the rocks to colorful coral reefs teeming with fish, 
              every day brings new discoveries.
            </p>
            <p>
              Our location provides easy access to some of Australia's most spectacular marine environments, 
              making it the perfect base for your island adventure.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Instagram Section */}
      {/* <section className="featured-section">
        <div className="content-container">
          <div className="section-header">
            <h2>FEATURED INSTA POST?</h2>
            <p className="section-subtitle">A sneak peak of Island Life</p>
            <button 
              className="section-link-button"
              onClick={() => onNavigate('gallery')}
            >
              browse our gallery →
            </button>
          </div>
        </div>
      </section> */}

      {/* Full Width Slideshow */}
      <section className="slideshow-section">
        <div className="slideshow-container">
          <div className="slideshow-wrapper">
            {slideshowImages.map((image, index) => (
              <div
                key={index}
                className={`slideshow-slide ${index === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url(${image})` }}
              />
            ))}
          </div>
          <div className="slideshow-controls">
            {slideshowImages.map((_, index) => (
              <button
                key={index}
                className={`slideshow-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

